import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import userStore from "stores/UserStore";
import DebugDisclaimer from "../DebugDisclaimer/DebugDisclaimer";
import appStore, { AppDefinition } from "stores/AppStore";

import { useNotification } from "components/UI/NotificationProvider";
import AlertModalTokenExpire from "components/UI/AlertModalTokenExpire";
import WindowManager from "components/Window/WindowManager";
import Dock from "../Dock/Dock";
import TopBar from "../TopBar/TopBar";
import { useCustomCursor } from "utils/useCustomCursor";
import { useMagneticHover } from "utils/useMagneticHover";
import IntroSection from "../IntroSection/IntroSection";
import { initFluid, toggleColorMode } from "utils/canvasCursor";

import { useIsMobile } from "hook/useIsMobile";
import WarningModal from "components/UI/WarningModal";

interface OSProps {
  onRequireAuth: () => void;
}

const OS: React.FC<OSProps> = observer(({ onRequireAuth }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();
  const auth0 = useAuth0();
  const notify = useNotification();
  const isMobile = useIsMobile();
  const [hasShownIntro, setHasShownIntro] = useState(false);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [hasDismissedWarning, setHasDismissedWarning] = useState(false);

  const {
    backgroundUrl,
    showPreboot,
    isAuthenticated,
    debugMode,
    showAlertModal,
  } = userStore;

  useEffect(() => {
    if (isMobile && !hasDismissedWarning) {
      setIsWarningModalOpen(true);
    } else {
      setIsWarningModalOpen(false);
    }
  }, [isMobile, hasDismissedWarning]);

  const handleWarningModalClose = () => {
    setHasDismissedWarning(true);
    setIsWarningModalOpen(false);
  };

  useCustomCursor();
  useMagneticHover();

  const handleIntroDone = () => {
    setHasShownIntro(true);
    setTimeout(() => {
      console.log("FluidEnabled");
    }, 100);
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    initFluid(canvasRef.current);

    userStore.initNetworkStatus(
      process.env.REACT_APP_API_URL || "https://google.fr"
    );

    window.addEventListener("mousemove", function handle(e) {
      window.dispatchEvent(
        new MouseEvent("mousedown", {
          clientX: e.clientX,
          clientY: e.clientY,
        })
      );
      window.removeEventListener("mousemove", handle);
    });
  }, []);

  useEffect(() => {
    if (userStore.isOnline) {
      toggleColorMode("default");
    } else {
      toggleColorMode("alt");
    }
  }, [userStore.isOnline]);

  const handleLaunchApp = (app: AppDefinition) => {
    if (app.requiresBackend && !userStore.isAuthenticated) {
      userStore.setPendingBackendApp(app.appName);
      userStore.setShowAuthModal(true);
      return;
    }

    appStore.launchApp(app.appName);
  };

  const shouldShowIntro =
    !hasShownIntro && !userStore.showAuthModal && !userStore.isAuthenticated;

  return (
    <div className="h-screen w-screen relative overflow-hidden bg-black/[0.96]">
      {shouldShowIntro && <IntroSection onIntroDone={handleIntroDone} />}

      {debugMode && (
        <div className="absolute w-full pointer-events-none z-20">
          <DebugDisclaimer />
        </div>
      )}

      <canvas
        ref={canvasRef}
        id="fluid"
        className="w-full h-full block z-[2] relative opacity-60"
      />

      <div className="cursor" />

      <div className="absolute inset-0 bg-customGray/40 z-0"></div>
      <div className="absolute inset-0 h-full w-full bg-[linear-gradient(to_right,#22dcd107_1px,transparent_1px),linear-gradient(to_bottom,#22dcd107_1px,transparent_1px)] bg-[size:160px_160px]" />

      {/* BACKGROUND */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
        <h1
          className={`
          uppercase leading-none mt-0 lg:mt-6 
          text-[8rem] sm:text-[11rem] md:text-[14rem] lg:text-[16rem] xl:text-[18rem] 
          select-none font-bold bg-clip-text text-transparent bg-gradient-to-b
          ${
            userStore.isOnline
              ? "from-synkColor/50 to-synkColor/90"
              : "from-[#C20E4D] to-red-600"
          }
        `}
        >
          SYNK
        </h1>
      </div>
      <div
        className="
            absolute inset-0
            bg-cover bg-center bg-no-repeat
            bg-blend-multiply
            z-[1]
          "
        style={{
          backgroundImage: `url("${userStore.backgroundUrl}")`,
        }}
      ></div>

      {/* Maintenance Alert */}
      {!userStore.isOnline && (
        <div className="absolute w-full flex justify-center top-6 z-40">
          <div
            role="alert"
            data-visible="true"
            data-has-title="true"
            title="This is a danger alert"
            className="flex flex-row w-auto py-3 px-4 gap-x-1 rounded-medium items-center text-danger-600 dark:text-danger-500 bg-danger-100/80 max-w-md"
          >
            <div className="flex-none relative w-9 h-9 rounded-full grid place-items-center bg-danger-50 dark:bg-danger-100 border-danger-100 shadow-small border-1">
              <svg
                className="fill-current w-6 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                fill="none"
                height="20"
                viewBox="0 0 20 20"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.51 3.85L11.57 0.42C10.6 -0.14 9.4 -0.14 8.42 0.42L2.49 3.85C1.52 4.41 0.919998 5.45 0.919998 6.58V13.42C0.919998 14.54 1.52 15.58 2.49 16.15L8.43 19.58C9.4 20.14 10.6 20.14 11.58 19.58L17.52 16.15C18.49 15.59 19.09 14.55 19.09 13.42V6.58C19.08 5.45 18.48 4.42 17.51 3.85ZM9.25 5.75C9.25 5.34 9.59 5 10 5C10.41 5 10.75 5.34 10.75 5.75V11C10.75 11.41 10.41 11.75 10 11.75C9.59 11.75 9.25 11.41 9.25 11V5.75ZM10.92 14.63C10.87 14.75 10.8 14.86 10.71 14.96C10.52 15.15 10.27 15.25 10 15.25C9.87 15.25 9.74 15.22 9.62 15.17C9.49 15.12 9.39 15.05 9.29 14.96C9.2 14.86 9.13 14.75 9.07 14.63C9.02 14.51 9 14.38 9 14.25C9 13.99 9.1 13.73 9.29 13.54C9.39 13.45 9.49 13.38 9.62 13.33C9.99 13.17 10.43 13.26 10.71 13.54C10.8 13.64 10.87 13.74 10.92 13.87C10.97 13.99 11 14.12 11 14.25C11 14.38 10.97 14.51 10.92 14.63Z"></path>
              </svg>
            </div>
            <div className="h-full flex-grow min-h-10 ms-2 flex flex-col box-border text-inherit justify-center items-center">
              <div className="text-small w-full font-bold block text-inherit leading-5 uppercase ">
                All our nodes are under maintenance
              </div>
            </div>
            <div className="flex-none relative w-4 h-9 rounded-full grid place-items-center"></div>
          </div>
        </div>
      )}

      <div className="absolute w-full flex justify-center z-30">
        <TopBar onRequireAuth={onRequireAuth} />
      </div>

      <WindowManager />

      <div className="z-50">
        <Dock onLaunchApp={handleLaunchApp} />
      </div>

      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={handleWarningModalClose}
        title="Notice"
        message="This application is not optimized for screens of this size. You can continue to use it, but performance may be degraded."
      />

      {showAlertModal && (
        <AlertModalTokenExpire
          isOpen={showAlertModal}
          onClose={() => userStore.closeAlertModal()}
        />
      )}
    </div>
  );
});

export default OS;
